// src/components/JarvislyModule/ModuleProvider.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import ListPreview from '../JarvislyModule/ListPreview';
import { useNavigate } from 'react-router-dom';
import { useModuleStore } from 'stores/module';
import ListFilter from '../JarvislyModule/ListFilter';
import { Modal, notification } from 'antd';
import translateX from 'utils/translateX';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import useZustand from 'hooks/useZustand';
import appService from 'services/appService';
import { apiUrl } from 'constants/Api';
import axiosService from 'services/axiosService';

// *******************************************************************************
// CONTEXT
// *******************************************************************************
const ModuleContext = createContext({});
export const useModuleContext = () => useContext(ModuleContext);

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ModuleProvider = ({ children }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { listSettings: storedListSettings } = useModuleStore();

  const setPageStore = useZustand('module', 'setPage');
  const storedCurrentPage = useZustand('module', 'currentPage');
  const storedModule = useZustand('module', 'config');
  const setPageBackStore = useZustand('module', 'setPageBack');
  const storedSubscription = useZustand('auth', 'subscription');
  const setGlobalStore = useZustand('global', 'setState');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const navigate = useNavigate();
  const notificationKeyRef = useRef('new-doc-editable');
  const [notificationApi, contextNotificationHolder] = notification.useNotification();
  const [modalApi, contextModalHolder] = Modal.useModal();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const ParametersComponent = storedModule?.parametersModalComponent;
  const data = storedCurrentPage?.data;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [listFloatActionsButton, setListFloatActionsButton] = useState({});
  const [providerContexts, setProviderContexts] = useState({
    listContext: null,
    documentContext: null,
    formContext: null,
    pageContext: null,
  });
  const [listHasNumberOfLineColumns, setListHasNumberOfLineColumns] = useState(false);

  const [selectedDocument, setSelectedDocument] = useState();
  const [showPreviewDrawer, setShowPreviewDrawer] = useState(false);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [showParametersModal, setShowParametersModal] = useState(false);
  const [parametersModalInitialTab, setParametersModalInitialTab] = useState(null);
  const [hasDrawerFilter, setHasDrawerFilter] = useState(false);
  const [documentState, setDocumentState] = useState({ mode: 'add' });
  const [actionRefreshed, setActionRefreshed] = useState(null);
  const [documentDuplicated, setActionDocumentDuplicated] = useState(null);
  const [filterDrawer, setFilterDrawer] = useState({});

  // Avatar initials state (for SimpleInput with useInitialInAvatar)
  const [avatarInitialState, setAvatarInitialState] = useState({
    avatarInitial: '',
    avatarInitialFirst: '',
    avatarInitialSecond: '',
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // NOTE: Removed Zustand sync for providerContexts to avoid Redux DevTools serialization issues
  // The formContext contains the large Ant Design form object which causes memory issues
  // Use React DevTools or useModuleContext() hook to inspect context values instead

  // Reset avatar initials ONLY when explicitly creating a new document (mode 'add')
  // Do NOT reset when document ID changes - this happens after save and would clear valid data
  useEffect(() => {
    if (documentState?.mode === 'add') {
      setAvatarInitialState({
        avatarInitial: '',
        avatarInitialFirst: '',
        avatarInitialSecond: '',
      });
    }
  }, [documentState?.mode]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handlePreviewDocument = useCallback(record => {
    setSelectedDocument(record);
    setShowPreviewDrawer(true);
  }, []);

  // Update the selected document in the preview drawer (e.g., after validation)
  const updateSelectedDocument = useCallback(updatedRecord => {
    setSelectedDocument(prev => (prev?._id === updatedRecord?._id ? updatedRecord : prev));
  }, []);

  // const handleRefreshDocument = useCallback(() => {
  //   // Re-fetch data from backend using the global API refresh function
  //   // Pass showLoading: false to prevent UI flash during refresh
  //   // This keeps current data visible while fetching new data
  //   // IMPORTANT: Must pass _id from current data since hookRef in useApiInitializer
  //   // may not have the correct _id when called from ModuleProvider context
  //   const _id = storedCurrentPage?.data?._id;
  //   if (_id) {
  //     apiGlobal?.refresh?.({ _id, showLoading: false });
  //   }
  // }, [apiGlobal, storedCurrentPage?.data?._id]);

  const handleShowFilterDrawer = useCallback(record => {
    setSelectedDocument(record);
    setShowFilterDrawer(true);
  }, []);

  // Open parameters modal with optional initial tab
  const openParametersModal = useCallback((initialTab = null) => {
    setParametersModalInitialTab(initialTab);
    setShowParametersModal(true);
  }, []);

  const setDocumentDuplicated = useCallback(
    (done, options = {}) => {
      const { skipNavigation = false } = options;

      // When done === true, we're just clearing the flag - never navigate
      // When done === false/undefined, we're setting the flag for duplication
      const shouldNavigate = !done && !skipNavigation;

      if (shouldNavigate) {
        setPageStore({
          navigate,
          pageName: storedCurrentPage.name,
          params: { _id: 'add' },
        });

        // keep legacy side effect (info to user)
        notificationApi.info({
          key: notificationKeyRef.current,
          message: translateX('document_duplicated'),
          description: translateX('document_duplicated_text'),
          duration: 15,
        });

        setDocumentState(prevState => ({ ...prevState, mode: 'add' }));
      }

      // Update BOTH local state AND zustand store to keep them in sync
      // SimpleFormProvider uses the store value to avoid stale closure issues
      setActionDocumentDuplicated(done ? null : +new Date());
      const { setDocumentDuplicatedStore } = useModuleStore.getState();
      setDocumentDuplicatedStore?.(done);
    },
    [navigate, notificationApi, setPageStore, storedCurrentPage?.name]
  );

  const setFireModuleAction = useCallback(mode => {
    setActionRefreshed(+new Date());
    setDocumentState(prevState => ({ ...prevState, mode }));
  }, []);

  /**
   * Registra o contexto de um provider específico no ModuleContext.
   * Substitui o antigo setFunctions() que misturava tudo em um único objeto.
   *
   * @param {'listContext' | 'documentContext' | 'formContext'} providerName - Nome do provider
   * @param {Object|null} context - Contexto/funções expostas pelo provider (null para limpar)
   *
   * @example
   * // No ListProvider:
   * registerProviderContext('listContext', { handleClearFilters, handleListRefresh });
   *
   * // No DocumentProvider:
   * registerProviderContext('documentContext', { viewMode, setViewMode });
   *
   * // No SimpleFormProvider:
   * registerProviderContext('formContext', { submitForm, resetForm, form });
   *
   * // Para usar no componente:
   * const { listContext, documentContext, formContext } = useModuleContext();
   * formContext.submitForm();
   * listContext.handleClearFilters();
   */
  const registerProviderContext = useCallback((providerName, context) => {
    const validProviders = ['listContext', 'documentContext', 'formContext', 'pageContext'];
    if (!validProviders.includes(providerName)) {
      console.error(
        `ModuleProvider: providerName inválido "${providerName}". Use: ${validProviders.join(', ')}`
      );
      return;
    }
    setProviderContexts(prev => ({
      ...prev,
      [providerName]: context,
    }));
  }, []);

  // Setter for avatar initials (used by SimpleInput with useInitialInAvatar)
  const setAvatarInitial = useCallback(updates => {
    if (typeof updates !== 'object') return;
    setAvatarInitialState(prev => ({ ...prev, ...updates }));
  }, []);

  // DEPRECATED: Mantido para compatibilidade retroativa. Use registerProviderContext() no lugar.
  const setFunctions = useCallback(
    objWithFunctions => {
      if (typeof objWithFunctions !== 'object') return;

      // Handle avatar initials separately (no deprecation warning for this specific use case)
      if (
        'avatarInitial' in objWithFunctions ||
        'avatarInitialFirst' in objWithFunctions ||
        'avatarInitialSecond' in objWithFunctions
      ) {
        setAvatarInitial(objWithFunctions);
        return;
      }

      console.warn(
        'DEPRECATED: setFunctions() será removido. Use registerProviderContext() no lugar.'
      );

      // Tenta identificar o provider pelo context
      if (objWithFunctions.context === 'list' || objWithFunctions.listContext) {
        registerProviderContext('listContext', objWithFunctions);
      } else if (objWithFunctions.context === 'document' || objWithFunctions.documentContext) {
        registerProviderContext('documentContext', objWithFunctions);
      } else if (objWithFunctions.form || objWithFunctions.submitForm) {
        registerProviderContext('formContext', objWithFunctions);
      }
    },
    [registerProviderContext, setAvatarInitial]
  );

  // DEPRECATED: Alias para setFunctions
  const setState = setFunctions;

  const handleEditDocument = useCallback(
    record => {
      setShowPreviewDrawer(false);
      setPageStore({
        navigate,
        pageName: 'document',
        params: { _id: record?._id || selectedDocument?._id },
      });
    },
    [navigate, selectedDocument, setPageStore]
  );

  const handleProcessDocument = useCallback(
    async (action, recordsArr, options = {}) => {
      const { onSuccess } = options;
      const folder = documentState?.folder || storedListSettings?.filterDrawer?.folder;
      const pageLayout = storedCurrentPage?.layout; // 'list' | 'document'
      const baseApiUrl = storedCurrentPage?.api?.url;

      // Fallback to current document if no records provided
      if (!recordsArr?.length) recordsArr = [storedCurrentPage?.data];

      // Filter out null/undefined records
      recordsArr = recordsArr.filter(Boolean);
      if (!recordsArr.length) return;

      let title;
      let content;
      let successMessage;
      let failureMessage;

      // Check if any company is the subscription main company (cannot be removed)
      const isSubscription = recordsArr?.find(x => x?._id === storedSubscription?.entityId);

      if (isSubscription) {
        appService.modal({
          flag: 'warning',
          title: 'remove_company_subscription_prohibit_title',
          content: (
            <div>
              <p>
                {translateX('remove_company_subscription_prohibit_info', {
                  company: <strong>{isSubscription?.name}</strong>,
                })}
              </p>
            </div>
          ),
          okText: 'close',
          centered: true,
          mask: { closable: true },
        });
        return;
      }

      // ────────────────────────────────────────────────────────────────────── //
      // DECLASSIFICATION LOGIC: For 'remove' action on entities with multiple profiles
      // Instead of deleting, just remove the current module's profile from dataProfile
      // ────────────────────────────────────────────────────────────────────── //
      if (action === 'remove' && folder !== 'recycle') {
        // Get current module's entityProfile from pagination filter (list pages)
        // or from api.query.filter (document pages - stored as JSON string)
        let moduleEntityProfile = storedCurrentPage?.pagination?.filter?.entityProfile || [];

        // For document pages, parse the filter from api.query.filter
        if (moduleEntityProfile.length === 0 && storedCurrentPage?.api?.query?.filter) {
          try {
            const parsedFilter = JSON.parse(storedCurrentPage.api.query.filter);
            moduleEntityProfile = parsedFilter?.entityProfile || [];
          } catch (e) {
            // Ignore parse errors
          }
        }

        if (moduleEntityProfile.length > 0) {
          // Separate records into declassify vs remove
          const recordsToDeclassify = [];
          const recordsToRemove = [];

          // Profiles to ignore when checking for declassification (these don't count as "real" profiles)
          // 'search' is a temporary profile added by dataSearch and should not prevent removal
          const ignoredProfiles = ['search'];

          for (const record of recordsArr) {
            // The field is called 'entityProfile' in the entities schema
            const recordProfiles = record.entityProfile || [];

            // Check if record has the current module's profile AND has other profiles
            // Ignore 'searched' profile when determining if record has other real profiles
            const hasCurrentProfile = moduleEntityProfile.some(p => recordProfiles.includes(p));
            const hasOtherProfiles = recordProfiles.some(
              p => !moduleEntityProfile.includes(p) && !ignoredProfiles.includes(p)
            );

            if (hasCurrentProfile && hasOtherProfiles) {
              recordsToDeclassify.push(record);
            } else {
              recordsToRemove.push(record);
            }
          }

          // Handle mixed scenario: some declassify, some remove
          if (recordsToDeclassify.length > 0 || recordsToRemove.length > 0) {
            const profileName = moduleEntityProfile[0]; // e.g., 'customer', 'company'

            // Build confirmation content based on what will happen
            let confirmContent;
            if (recordsToDeclassify.length > 0 && recordsToRemove.length > 0) {
              // Mixed: some will be declassified, some will be removed
              confirmContent = (
                <div>
                  <p>
                    {translateX('declassify_mixed_info', {
                      declassifyQty: recordsToDeclassify.length,
                      removeQty: recordsToRemove.length,
                      profile: translateX(profileName),
                    })}
                  </p>
                </div>
              );
            } else if (recordsToDeclassify.length > 0) {
              // All will be declassified
              confirmContent = (
                <div>
                  <p>
                    {translateX(
                      recordsToDeclassify.length > 1
                        ? 'declassify_multi_document_ask'
                        : 'declassify_document_ask',
                      {
                        qty: recordsToDeclassify.length,
                        profile: translateX(profileName),
                      }
                    )}
                  </p>
                </div>
              );
            }

            // If there are records to declassify, show the declassify confirmation
            if (recordsToDeclassify.length > 0) {
              const onlyDeclassify = recordsToRemove.length === 0;

              modalApi.confirm({
                title: translateX(onlyDeclassify ? 'declassify_document_title' : 'remove_document_title'),
                icon: <ExclamationCircleOutlined />,
                content: confirmContent,
                okText: translateX('confirm'),
                okType: 'primary',
                okButtonProps: { danger: true },
                cancelText: translateX('cancel'),
                onOk: async () => {
                  try {
                    const url = apiUrl.documentProcess(baseApiUrl);

                    // Declassify records (remove profile from dataProfile)
                    if (recordsToDeclassify.length > 0) {
                      await axiosService.post({
                        url,
                        data: {
                          action: 'declassify',
                          ids: recordsToDeclassify.map(x => x._id),
                          profileToRemove: moduleEntityProfile,
                        },
                      });
                    }

                    // Remove records that only have the current profile
                    if (recordsToRemove.length > 0) {
                      await axiosService.post({
                        url,
                        data: {
                          action: 'remove',
                          ids: recordsToRemove.map(x => x._id),
                        },
                      });
                    }

                    // Show appropriate success notification
                    if (recordsToDeclassify.length > 0 && recordsToRemove.length > 0) {
                      appService.notification({
                        flag: 's',
                        title: 'operation_completed',
                        description: translateX('declassify_mixed_success', {
                          declassifyQty: recordsToDeclassify.length,
                          removeQty: recordsToRemove.length,
                          profile: translateX(profileName),
                        }),
                        duration: 6,
                      });
                    } else if (recordsToDeclassify.length > 0) {
                      appService.notification({
                        flag: 's',
                        title: 'document_declassified',
                        description: translateX(
                          recordsToDeclassify.length > 1
                            ? 'declassify_multi_success'
                            : 'declassify_success',
                          {
                            qty: recordsToDeclassify.length,
                            profile: translateX(profileName),
                          }
                        ),
                        duration: 6,
                      });
                    }

                    // Execute callback if provided (e.g., refresh list)
                    if (typeof onSuccess === 'function') {
                      await onSuccess();
                    }

                    // For document pages, navigate back after successful action
                    if (pageLayout === 'document') {
                      setPageBackStore({ navigate });
                    }
                  } catch (error) {
                    appService.message({ flag: 'e', text: 'remove_document_failure' });
                    console.error('handleProcessDocument error:', error);
                  }
                },
              });

              return; // Exit early - we handled the mixed/declassify case
            }
          }
        }
      }
      // ────────────────────────────────────────────────────────────────────── //
      // END DECLASSIFICATION LOGIC
      // ────────────────────────────────────────────────────────────────────── //

      // Build confirmation dialog content based on action
      if (action === 'remove') {
        if (folder === 'recycle') {
          title = translateX('permanently_remove');
          content = translateX(
            recordsArr?.length > 1
              ? 'permanently_multi_remove_document_ask'
              : 'permanently_remove_document_ask',
            { qty: recordsArr.length }
          );
        } else {
          title = translateX('remove_document_title');
          content = translateX(
            recordsArr?.length > 1 ? 'remove_multi_document_ask' : 'remove_document_ask',
            { qty: recordsArr.length }
          );
        }

        successMessage = 'remove_document_success';
        failureMessage = 'remove_document_failure';
      } else if (action === 'archive') {
        title = translateX('file_document_title');
        content = translateX(
          recordsArr?.length > 1 ? 'file_multi_document_ask' : 'file_document_ask',
          { qty: recordsArr.length }
        );
        successMessage = 'document_archived';
        failureMessage = 'file_document_failure';
      } else {
        title = translateX('restore_document');
        content = translateX(
          recordsArr?.length > 1 ? 'restore_multi_document_ask' : 'restore_document_ask',
          { qty: recordsArr.length }
        );
        successMessage = 'document_restored';
        failureMessage = 'restore_document_failure';
      }

      modalApi.confirm({
        title,
        icon: <ExclamationCircleOutlined />,
        content,
        okText: translateX('confirm'),
        okType: 'primary',
        okButtonProps: action === 'remove' ? { danger: true } : undefined,
        cancelText: translateX('cancel'),
        onOk: async () => {
          try {
            const dataSet = {
              action,
              ids: recordsArr.map(x => x._id),
            };

            // Build document-process URL from base API URL
            const url = apiUrl.documentProcess(baseApiUrl);

            await axiosService.post({ url, data: dataSet });

            // Show success message
            appService.message({ flag: 's', text: successMessage });

            // Execute callback if provided (e.g., refresh list)
            if (typeof onSuccess === 'function') {
              await onSuccess();
            }

            // For document pages, navigate back after successful action
            if (pageLayout === 'document') {
              setPageBackStore({ navigate });
            }
          } catch (error) {
            appService.message({ flag: 'e', text: failureMessage });
            console.error('handleProcessDocument error:', error);
          }
        },
      });
    },
    [
      documentState?.folder,
      storedListSettings?.filterDrawer?.folder,
      storedCurrentPage?.layout,
      storedCurrentPage?.api?.url,
      storedCurrentPage?.api?.query?.filter,
      storedCurrentPage?.data,
      storedCurrentPage?.pagination?.filter?.entityProfile,
      storedSubscription?.entityId,
      modalApi,
      navigate,
      setPageBackStore,
    ]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const exposedValues = useMemo(
    () => ({
      // ─── PROVIDER CONTEXTS (NAMESPACES ORGANIZADOS) ───
      // Acesso organizado às funções de cada provider
      registerProviderContext,
      listContext: providerContexts.listContext,
      documentContext: providerContexts.documentContext,
      formContext: providerContexts.formContext,
      pageContext: providerContexts.pageContext,

      // ─── DEPRECATED: Compatibilidade retroativa ───
      // Será removido em versões futuras
      setFunctions, // use registerProviderContext
      setState: setFunctions, // use registerProviderContext
      state: providerContexts, // use listContext, documentContext, formContext diretamente

      // ─── SPREAD DAS FUNÇÕES DO FORM (compatibilidade) ───
      // REMOVIDO POR PERFORMANCE: O spread causava re-render cascata em toda a aplicação
      // Use formContext.submitForm(), formContext.goFocus(), etc. diretamente
      // ...(providerContexts.formContext || {}),

      // ─── AVATAR INITIALS STATE ───
      // Used by SimpleInput with useInitialInAvatar prop
      avatarInitial: avatarInitialState.avatarInitial,
      avatarInitialFirst: avatarInitialState.avatarInitialFirst,
      avatarInitialSecond: avatarInitialState.avatarInitialSecond,
      setAvatarInitial,

      // ─── MODULE PROVIDER STATE ───
      listFloatActionsButton,
      setListFloatActionsButton,
      listHasNumberOfLineColumns,
      setListHasNumberOfLineColumns,
      selectedDocument,
      handleEditDocument,
      mode: documentState?.mode,
      setDocumentState,
      documentState,
      actionRefreshed,
      documentDuplicated,
      setDocumentDuplicated,
      handlePreviewDocument,
      handleShowFilterDrawer,
      setShowParametersModal,
      showParametersModal,
      openParametersModal,
      parametersModalInitialTab,
      hasDrawerFilter,
      setHasDrawerFilter,
      filterDrawer,

      // ─── BATCH DOCUMENT ACTIONS ───
      // recordsArr: array of documents to process (uses current document if empty)
      // options: { onSuccess: () => void } - callback after successful action
      handleArchiveDocuments: (recordsArr, options) =>
        handleProcessDocument('archive', recordsArr, options),
      handleRemoveDocuments: (recordsArr, options) =>
        handleProcessDocument('remove', recordsArr, options),
      handleRestoreDocuments: (recordsArr, options) =>
        handleProcessDocument('restore', recordsArr, options),

      data,
    }),
    [
      providerContexts,
      registerProviderContext,
      avatarInitialState,
      setAvatarInitial,
      listFloatActionsButton,
      listHasNumberOfLineColumns,
      selectedDocument,
      documentState,
      actionRefreshed,
      documentDuplicated,
      showParametersModal,
      hasDrawerFilter,
      filterDrawer,
      setFireModuleAction,
      handlePreviewDocument,
      handleShowFilterDrawer,
      handleEditDocument,
      setFunctions,
      handleProcessDocument,
      setDocumentDuplicated,
      data,
    ]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <ModuleContext.Provider value={exposedValues}>
      {contextModalHolder}
      {contextNotificationHolder}
      {children}

      {/* PREVIEW LEFT DRAWER */}
      <ListPreview
        setShow={setShowPreviewDrawer}
        show={showPreviewDrawer}
        selectedDocument={selectedDocument}
        updateSelectedDocument={updateSelectedDocument}
        handleEditDocument={handleEditDocument}
        handleArchiveDocuments={(recordsArr, options) =>
          handleProcessDocument('archive', recordsArr, options)
        }
        handleRemoveDocuments={(recordsArr, options) =>
          handleProcessDocument('remove', recordsArr, options)
        }
        handleRestoreDocuments={(recordsArr, options) =>
          handleProcessDocument('restore', recordsArr, options)
        }
        folder={storedListSettings?.filterDrawer?.folder || 'documents'}
        refresh={providerContexts.listContext?.refresh}
      />
      {/* PREVIEW LEFT DRAWER */}

      {/* FILTER RIGHT DRAWER */}
      <ListFilter
        setShow={setShowFilterDrawer}
        show={showFilterDrawer}
        selectedDocument={selectedDocument}
      />
      {/* FILTER RIGHT DRAWER */}

      {/* PARAMETERS MODAL */}
      {ParametersComponent && (
        <ParametersComponent
          setShow={setShowParametersModal}
          show={showParametersModal}
          initialTab={parametersModalInitialTab}
          resetInitialTab={() => setParametersModalInitialTab(null)}
        />
      )}
      {/* PARAMETERS MODAL */}
    </ModuleContext.Provider>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ModuleProvider;

// *******************************************************************************
// HELPERS
// *******************************************************************************
// (Nenhuma alteração necessária aqui)
